import React, { useState, useEffect, useRef, useCallback } from 'react';
import { groupAPI } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Send, MessageCircle, Crown, Trash2, ChevronDown, Users, AtSign, Wifi, WifiOff, Paperclip, FileText, Image, X as XIcon, Loader2, Upload, File, ImageIcon, Download } from 'lucide-react';
import { toast } from 'sonner';
import { format, isToday, isYesterday, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';

export default function GroupChatDialog({ 
  open, 
  onClose, 
  classId, 
  group, 
  currentUser,
  isProfessor = false 
}) {
  const [messages, setMessages] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [hasMore, setHasMore] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const [showMentions, setShowMentions] = useState(false);
  const [mentionSearch, setMentionSearch] = useState('');
  const [mentionPosition, setMentionPosition] = useState(0);
  const [typingUsers, setTypingUsers] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  
  const messagesEndRef = useRef(null);
  const scrollAreaRef = useRef(null);
  const wsRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const dragCounter = useRef(0);

  const loadMessages = useCallback(async (before = null) => {
    if (!group) return;
    
    try {
      const res = await groupAPI.getMessages(classId, group.id, 50, before);
      if (before) {
        setMessages(prev => [...res.data.messages, ...prev]);
      } else {
        setMessages(res.data.messages);
      }
      setHasMore(res.data.has_more);
    } catch (error) {
      if (!before) {
        toast.error('Error al cargar mensajes');
      }
    } finally {
      setLoading(false);
    }
  }, [classId, group]);

  const loadMembers = useCallback(async () => {
    if (!group) return;
    try {
      const res = await groupAPI.getMembers(classId, group.id);
      setMembers(res.data || []);
    } catch (error) {
      console.error('Error loading members:', error);
    }
  }, [classId, group]);

  // WebSocket connection
  useEffect(() => {
    if (!open || !group || !currentUser) return;

    const token = localStorage.getItem('token');
    if (!token) return;

    // Get WebSocket URL from backend URL
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    const wsProtocol = backendUrl.startsWith('https') ? 'wss' : 'ws';
    const wsHost = backendUrl.replace(/^https?:\/\//, '');
    const wsUrl = `${wsProtocol}://${wsHost}/api/ws/groups/${classId}/${group.id}?token=${token}`;

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('WebSocket connected');
        setWsConnected(true);
      };

      ws.onmessage = (event) => {
        const data = JSON.parse(event.data);
        
        if (data.type === 'new_message') {
          setMessages(prev => [...prev, data.message]);
          // Scroll to bottom
          setTimeout(() => {
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
          }, 100);
        } else if (data.type === 'typing') {
          setTypingUsers(prev => {
            if (!prev.includes(data.user_name)) {
              return [...prev, data.user_name];
            }
            return prev;
          });
          // Remove typing indicator after 3 seconds
          setTimeout(() => {
            setTypingUsers(prev => prev.filter(name => name !== data.user_name));
          }, 3000);
        } else if (data.type === 'notification') {
          // Handle real-time notification
          toast.info(data.notification.title);
        }
      };

      ws.onclose = () => {
        console.log('WebSocket disconnected');
        setWsConnected(false);
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        setWsConnected(false);
      };

      return () => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.close();
        }
      };
    } catch (error) {
      console.error('WebSocket connection failed:', error);
    }
  }, [open, group, classId, currentUser]);

  useEffect(() => {
    if (open && group) {
      setLoading(true);
      loadMessages();
      loadMembers();
    }
    
    return () => {
      setMessages([]);
      setTypingUsers([]);
      setPendingFile(null);
      setIsDragging(false);
      dragCounter.current = 0;
    };
  }, [open, group, loadMessages, loadMembers]);

  useEffect(() => {
    // Scroll to bottom when messages change
    if (messagesEndRef.current && !loading) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, loading]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;
    
    // Extract mentions from message
    const mentionRegex = /@(\w+)/g;
    const mentionedNames = [];
    let match;
    while ((match = mentionRegex.exec(newMessage)) !== null) {
      mentionedNames.push(match[1].toLowerCase());
    }
    
    // Find user IDs for mentioned users
    const mentionedUserIds = members
      .filter(m => mentionedNames.some(name => 
        m.user_name.toLowerCase().includes(name) ||
        m.user_name.split(' ')[0].toLowerCase() === name
      ))
      .map(m => m.user_id);
    
    // Send via WebSocket if connected
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'message',
        content: newMessage.trim(),
        mentions: mentionedUserIds
      }));
      setNewMessage('');
    } else {
      // Fallback to REST API
      setSending(true);
      try {
        await groupAPI.sendMessage(classId, group.id, newMessage.trim());
        setNewMessage('');
        await loadMessages();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Error al enviar mensaje');
      } finally {
        setSending(false);
      }
    }
  };

  const processFile = (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('El archivo no puede superar 10MB');
      return;
    }
    const preview = { file, name: file.name, size: file.size, type: file.type, isImage: file.type?.startsWith('image/') };
    if (preview.isImage) {
      const reader = new FileReader();
      reader.onload = (e) => { preview.thumbnailUrl = e.target.result; setPendingFile({ ...preview }); };
      reader.readAsDataURL(file);
    } else {
      setPendingFile(preview);
    }
  };

  const handleFileInput = (e) => {
    processFile(e.target.files?.[0]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const uploadPendingFile = async () => {
    if (!pendingFile) return;
    setUploading(true);
    setUploadProgress(10);
    try {
      const formData = new FormData();
      formData.append('file', pendingFile.file);
      setUploadProgress(40);
      await groupAPI.uploadFile(classId, group.id, formData);
      setUploadProgress(100);
      await loadMessages();
      setPendingFile(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al subir archivo');
    } finally {
      setUploading(false);
      setUploadProgress(0);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Drag & Drop
  const handleDragEnter = (e) => { e.preventDefault(); e.stopPropagation(); dragCounter.current++; if (e.dataTransfer.types.includes('Files')) setIsDragging(true); };
  const handleDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); dragCounter.current--; if (dragCounter.current === 0) setIsDragging(false); };
  const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); };
  const handleDrop = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); dragCounter.current = 0; const file = e.dataTransfer.files?.[0]; if (file) processFile(file); };

  const handlePaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of items) {
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        const file = item.getAsFile();
        if (file) processFile(file);
        return;
      }
    }
  };

  const handleTyping = () => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      // Clear existing timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      
      // Send typing indicator
      wsRef.current.send(JSON.stringify({ type: 'typing' }));
      
      // Set timeout to stop sending
      typingTimeoutRef.current = setTimeout(() => {
        typingTimeoutRef.current = null;
      }, 2000);
    }
  };

  const handleDeleteMessage = async (messageId) => {
    if (!window.confirm('¿Eliminar este mensaje?')) return;
    
    try {
      await groupAPI.deleteMessage(classId, group.id, messageId);
      setMessages(prev => prev.filter(m => m.id !== messageId));
      toast.success('Mensaje eliminado');
    } catch (error) {
      toast.error('Error al eliminar mensaje');
    }
  };

  const loadMoreMessages = async () => {
    if (messages.length === 0 || !hasMore) return;
    const oldestMessage = messages[0];
    await loadMessages(oldestMessage.created_at);
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setNewMessage(value);
    handleTyping();
    
    // Check for @ mentions
    const cursorPos = e.target.selectionStart;
    const textBeforeCursor = value.substring(0, cursorPos);
    const atMatch = textBeforeCursor.match(/@(\w*)$/);
    
    if (atMatch) {
      setShowMentions(true);
      setMentionSearch(atMatch[1].toLowerCase());
      setMentionPosition(cursorPos - atMatch[0].length);
    } else {
      setShowMentions(false);
    }
  };

  const insertMention = (member) => {
    const beforeMention = newMessage.substring(0, mentionPosition);
    const afterMention = newMessage.substring(mentionPosition + mentionSearch.length + 1);
    const firstName = member.user_name.split(' ')[0];
    setNewMessage(`${beforeMention}@${firstName} ${afterMention}`);
    setShowMentions(false);
    inputRef.current?.focus();
  };

  const filteredMembers = members.filter(m => 
    m.user_id !== currentUser?.id &&
    (mentionSearch === '' || 
     m.user_name.toLowerCase().includes(mentionSearch))
  );

  const formatMessageTime = (dateStr) => {
    const date = parseISO(dateStr);
    if (isToday(date)) {
      return format(date, 'HH:mm');
    } else if (isYesterday(date)) {
      return `Ayer ${format(date, 'HH:mm')}`;
    } else {
      return format(date, 'd MMM HH:mm', { locale: es });
    }
  };

  const isOwnMessage = (message) => {
    return message.sender_user_id === currentUser?.id;
  };

  const renderMessageContent = (content) => {
    // Highlight mentions
    const parts = content.split(/(@\w+)/g);
    return parts.map((part, i) => {
      if (part.startsWith('@')) {
        return (
          <span key={i} className="bg-blue-100 text-blue-700 px-1 rounded font-medium">
            {part}
          </span>
        );
      }
      return part;
    });
  };

  if (!group) return null;

  return (
    <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl h-[90vh] sm:h-[80vh] flex flex-col p-0 sm:rounded-lg rounded-none w-full sm:max-w-2xl">
        {/* Header */}
        <DialogHeader className="px-3 sm:px-4 py-3 border-b bg-slate-50">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
              <div 
                className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: `${group.color}20` }}
              >
                <MessageCircle className="w-4 h-4 sm:w-5 sm:h-5" style={{ color: group.color }} />
              </div>
              <div className="min-w-0">
                <DialogTitle className="font-heading text-base sm:text-lg truncate">{group.name}</DialogTitle>
                <div className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm text-slate-500">
                  <Users className="w-3 h-3 flex-shrink-0" />
                  <span>{group.member_count} miembros</span>
                  {group.leader_name && (
                    <>
                      <span className="text-slate-300">|</span>
                      <Crown className="w-3 h-3 text-amber-500" />
                      <span>{group.leader_name}</span>
                    </>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {wsConnected ? (
                <Badge variant="outline" className="text-green-600 border-green-200 flex items-center gap-1">
                  <Wifi className="w-3 h-3" />
                  En vivo
                </Badge>
              ) : (
                <Badge variant="outline" className="text-slate-400 border-slate-200 flex items-center gap-1">
                  <WifiOff className="w-3 h-3" />
                  Offline
                </Badge>
              )}
              {isProfessor && (
                <Badge variant="outline" className="text-blue-600 border-blue-200">
                  Supervisando
                </Badge>
              )}
            </div>
          </div>
        </DialogHeader>

        {/* Messages Area with Drag & Drop */}
        <div
          className="flex-1 overflow-hidden relative"
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          {isDragging && (
            <div className="absolute inset-0 z-10 bg-blue-50/90 backdrop-blur-sm border-2 border-dashed border-blue-400 rounded-lg flex flex-col items-center justify-center pointer-events-none" data-testid="group-drag-overlay">
              <Upload className="w-10 h-10 text-blue-500 mb-2 animate-bounce" />
              <p className="text-sm font-semibold text-blue-600">Suelta el archivo aquí</p>
              <p className="text-xs text-blue-400">Máximo 10MB</p>
            </div>
          )}
          <ScrollArea className="h-full px-4" ref={scrollAreaRef}>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : messages.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-slate-400">
                <MessageCircle className="w-12 h-12 mb-3 opacity-50" />
                <p className="font-medium">No hay mensajes</p>
                <p className="text-sm">Sé el primero en escribir</p>
              </div>
            ) : (
              <div className="py-4 space-y-3">
                {hasMore && (
                  <div className="text-center">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={loadMoreMessages}
                      className="text-slate-500"
                    >
                      <ChevronDown className="w-4 h-4 mr-1 rotate-180" />
                      Cargar mensajes anteriores
                    </Button>
                  </div>
                )}
                
                {messages.map((message, index) => {
                  const isOwn = isOwnMessage(message);
                  const showAvatar = index === 0 || 
                    messages[index - 1].sender_user_id !== message.sender_user_id;
                  
                  return (
                    <div 
                      key={message.id}
                      className={`flex gap-2 ${isOwn ? 'flex-row-reverse' : 'flex-row'}`}
                    >
                      {/* Avatar */}
                      {showAvatar ? (
                        <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center overflow-hidden flex-shrink-0">
                          {message.sender_photo_url ? (
                            <img src={message.sender_photo_url} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <span className="text-xs font-medium text-slate-600">
                              {message.sender_name?.charAt(0).toUpperCase()}
                            </span>
                          )}
                        </div>
                      ) : (
                        <div className="w-8 flex-shrink-0" />
                      )}
                      
                      {/* Message Bubble */}
                      <div className={`group max-w-[70%] ${isOwn ? 'items-end' : 'items-start'}`}>
                        {showAvatar && (
                          <div className={`flex items-center gap-1 mb-1 ${isOwn ? 'justify-end' : 'justify-start'}`}>
                            <span className="text-xs font-medium text-slate-600">
                              {message.sender_name}
                            </span>
                            {message.is_leader && (
                              <Crown className="w-3 h-3 text-amber-500" />
                            )}
                            {message.is_professor && (
                              <Badge className="text-xs bg-blue-50 text-blue-600 px-1 py-0">Prof</Badge>
                            )}
                          </div>
                        )}
                        <div className="flex items-end gap-1">
                          <div 
                            className={`px-3 py-2 rounded-2xl ${
                              isOwn 
                                ? 'bg-blue-500 text-white rounded-br-md' 
                                : 'bg-slate-100 text-slate-800 rounded-bl-md'
                            }`}
                          >
                            {/* Image attachment */}
                            {message.message_type === 'image' && message.file_url && (
                              <a href={message.file_url} target="_blank" rel="noopener noreferrer" className="block mb-1">
                                <img src={message.file_url} alt={message.file_name || 'Imagen'} className="max-w-[240px] max-h-[200px] rounded-lg object-cover" />
                              </a>
                            )}
                            {/* File attachment */}
                            {message.message_type === 'file' && message.file_url && (
                              <a
                                href={message.file_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`flex items-center gap-2 px-2 py-1.5 rounded-lg mb-1 ${
                                  isOwn ? 'bg-blue-600/40 hover:bg-blue-600/60' : 'bg-slate-200/60 hover:bg-slate-200'
                                }`}
                                data-testid="file-attachment-link"
                              >
                                <FileText className="w-5 h-5 flex-shrink-0" />
                                <span className="text-xs font-medium truncate max-w-[180px]">{message.file_name || 'Archivo'}</span>
                              </a>
                            )}
                            {/* Text content */}
                            {message.content && (
                              <p className="text-sm whitespace-pre-wrap break-words">
                                {renderMessageContent(message.content)}
                              </p>
                            )}
                          </div>
                          
                          {/* Delete button */}
                          {(isOwn || isProfessor) && (
                            <button
                              onClick={() => handleDeleteMessage(message.id)}
                              className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-50 rounded transition-opacity"
                              title="Eliminar mensaje"
                            >
                              <Trash2 className="w-3 h-3 text-red-400 hover:text-red-600" />
                            </button>
                          )}
                        </div>
                        <p className={`text-xs text-slate-400 mt-0.5 ${isOwn ? 'text-right' : 'text-left'}`}>
                          {formatMessageTime(message.created_at)}
                        </p>
                      </div>
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>
            )}
          </ScrollArea>
        </div>

        {/* Typing indicator */}
        {typingUsers.length > 0 && (
          <div className="px-4 py-1 text-xs text-slate-400">
            {typingUsers.join(', ')} {typingUsers.length === 1 ? 'está escribiendo...' : 'están escribiendo...'}
          </div>
        )}

        {/* File Preview */}
        {pendingFile && (
          <div className="px-3 py-2 border-t border-slate-100 bg-slate-50" data-testid="group-file-preview">
            <div className="flex items-center gap-3 p-2 bg-white rounded-xl border border-slate-200">
              {pendingFile.isImage && pendingFile.thumbnailUrl ? (
                <img src={pendingFile.thumbnailUrl} alt="" className="w-12 h-12 rounded-lg object-cover flex-shrink-0" />
              ) : (
                <div className="w-12 h-12 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                  <File className="w-6 h-6 text-blue-500" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-800 truncate">{pendingFile.name}</p>
                <p className="text-xs text-slate-400">{pendingFile.size < 1048576 ? `${(pendingFile.size / 1024).toFixed(1)} KB` : `${(pendingFile.size / 1048576).toFixed(1)} MB`}</p>
              </div>
              {uploading ? (
                <div className="flex items-center gap-2 flex-shrink-0">
                  <div className="w-20 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                    <div className="h-full bg-blue-500 rounded-full transition-all duration-300" style={{ width: `${uploadProgress}%` }} />
                  </div>
                  <span className="text-xs text-slate-400">{uploadProgress}%</span>
                </div>
              ) : (
                <div className="flex items-center gap-1 flex-shrink-0">
                  <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-400 hover:text-red-500" onClick={() => setPendingFile(null)} data-testid="group-cancel-file-btn">
                    <XIcon className="w-4 h-4" />
                  </Button>
                  <Button size="icon" className="h-8 w-8 bg-blue-500 hover:bg-blue-600" onClick={uploadPendingFile} data-testid="group-confirm-upload-btn">
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Input Area */}
        <div className="px-3 sm:px-4 py-2 sm:py-3 border-t bg-white relative">
          {/* Mentions Popup */}
          {showMentions && filteredMembers.length > 0 && (
            <div className="absolute bottom-full left-3 right-3 sm:left-4 sm:right-4 mb-2 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
              <div className="p-2 border-b border-slate-100">
                <p className="text-xs text-slate-500 flex items-center gap-1">
                  <AtSign className="w-3 h-3" />
                  Mencionar a un miembro
                </p>
              </div>
              {filteredMembers.map(member => (
                <button
                  key={member.user_id}
                  onClick={() => insertMention(member)}
                  className="w-full flex items-center gap-2 px-3 py-2 hover:bg-slate-50 text-left"
                >
                  <div className="w-6 h-6 rounded-full bg-slate-200 flex items-center justify-center overflow-hidden">
                    {member.photo_url ? (
                      <img src={member.photo_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-xs">{member.user_name?.charAt(0)}</span>
                    )}
                  </div>
                  <span className="text-sm font-medium">{member.user_name}</span>
                  {member.is_leader && <Crown className="w-3 h-3 text-amber-500" />}
                </button>
              ))}
            </div>
          )}
          
          <form onSubmit={(e) => { e.preventDefault(); if (pendingFile) uploadPendingFile(); else handleSendMessage(e); }} className="flex gap-2">
            <input
              type="file"
              ref={fileInputRef}
              className="hidden"
              onChange={handleFileInput}
              accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.rvt,.dwg,.zip,.txt,.csv"
              data-testid="group-file-input"
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-10 w-10 flex-shrink-0 text-slate-400 hover:text-blue-500"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              data-testid="group-attach-btn"
            >
              {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Paperclip className="w-5 h-5" />}
            </Button>
            <Input
              ref={inputRef}
              value={newMessage}
              onChange={handleInputChange}
              onPaste={handlePaste}
              placeholder={isProfessor ? "Escribir como profesor... (usa @ para mencionar)" : "Escribe un mensaje... (usa @ para mencionar)"}
              className="flex-1"
              disabled={sending || uploading}
              data-testid="chat-input"
            />
            <Button 
              type="submit" 
              disabled={(!newMessage.trim() && !pendingFile) || sending || uploading}
              className="bg-blue-500 hover:bg-blue-600"
              data-testid="send-message-btn"
            >
              <Send className="w-4 h-4" />
            </Button>
          </form>
          <p className="text-xs text-slate-300 mt-1 text-center">
            Arrastra archivos, pega imágenes o usa @ para mencionar
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
